import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { fetchPublicDealer, searchProducts, lookupPricing, syncCart, fetchCart, getStripeStatus, guestSyncCart, recordShareClick } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useHouseBrand } from "@/hooks/useHouseBrand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import CartSheet from "@/components/DealerShop";
import { sanitizeHtml } from "@/lib/sanitize";
import PostTabs from "@/components/PostTabs";
import BrandHero from "@/components/BrandHero";
import ShopFilterHero, { categoriesFor, filterShopProducts } from "@/components/ShopFilterHero";
import ShareMenu from "@/components/ShareMenu";
import TagsMenuBar from "@/components/TagsMenuBar";
import { DealerFormView, DealerQaView } from "@/components/DealerFormAndQa";
import ProtocolBuilder, { focusFromSearch } from "@/components/ProtocolBuilder";
import ProductDetailContent from "@/components/ProductDetailContent";
import FeaturedSidebar from "@/components/FeaturedSidebar";
import EventsFeaturedBox from "@/components/EventsFeaturedBox";
import ReviewsSidebar from "@/components/ReviewsSidebar";
import VideoSidebar from "@/components/VideoSidebar";
import PhotosSidebar from "@/components/PhotosSidebar";
import {
  Loader2,
  AlertCircle,
  Mail,
  MapPin,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Package,
  ShoppingCart,
  ShoppingBag,
  ChefHat,
  Home as HomeIcon,
  Sparkles,
  ArrowRight as ArrowRightIcon,
  MessageCircle,
  Star,
  ChevronRight,
  CalendarDays, UserRound } from "lucide-react";

// Home + About Us merge: when the dealer has an About Us, insert it as an open
// "About Us" panel right below the hero, ABOVE the formula's first collapsible
// ("Home"). The Airtable home_html itself is left untouched.
export function mergeAboutIntoHome(homeHtml, aboutHtml, company = "") {
  const home = homeHtml || "";
  const about = (aboutHtml || "").trim();
  if (!about) return home;
  const title = company ? `About ${company}` : "About Us";
  const panel =
    `<details open data-about-merged="1" style="margin-bottom:12px;border:1px solid #dce8e4;border-radius:18px;background:#ffffff;overflow:hidden;">` +
    `<summary style="cursor:pointer;list-style:none;background:#b88a2b;color:#ffffff;padding:16px 20px;font-size:18px;font-weight:700;">${title}</summary>` +
    `<div style="padding:8px 6px 14px;">${about}</div></details>`;
  const i = home.search(/<details\b/i);
  if (i >= 0) return home.slice(0, i) + panel + home.slice(i);
  return `<div style="padding:22px 22px 8px;">${panel}</div>${home}`;
}

export default function DealerPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const brand = useHouseBrand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [shopBrand, setShopBrand] = useState("orevital");
  // Logged-in approved dealers share links under THEIR handle; visitors share this page's slug.
  const myHandle = (() => {
    if (!user || Number(user.approved ?? user.is_approved ?? 0) !== 1) return null;
    if (user.slug) return user.slug;
    if (user.user_name) return String(user.user_name).replace(/[^a-zA-Z0-9._-]+/g, "").toLowerCase() || null;
    const parts = [user.first_name, user.last_name].map((x) => (x || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")).filter(Boolean);
    return parts.length && user.user_id ? `${parts.join("-")}-${user.user_id}` : null;
  })();
  const shareHandle = myHandle || slug;
  function productShareUrl(p) {
    const ps = p.slug || p.id;
    return `https://orevital.com/d/${shareHandle}?view=product&p=${encodeURIComponent(ps)}&ref=share`;
  }

  // Shared-link attribution: remember the referral for 30 days + count the click once.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("ref") !== "share" || !slug) return;
    const productSlug = q.get("p") || "";
    try { localStorage.setItem("postcue.share_ref", JSON.stringify({ dealer_slug: slug, product_slug: productSlug, at: Date.now() })); } catch { /* ignore */ }
    recordShareClick(slug, productSlug).catch(() => {});
    q.delete("ref");
    try { window.history.replaceState(null, "", `${window.location.pathname}${q.toString() ? `?${q}` : ""}`); } catch { /* ignore */ }
  }, [slug]);
  const [shopCategory, setShopCategory] = useState(null);
  const [shopQuery, setShopQuery] = useState("");
  // A typed search looks across BOTH brands, whichever icon is picked.
  const searching = shopQuery.trim().length > 0;
  const brandProducts = filterShopProducts(products, { brand: searching ? null : shopBrand, query: shopQuery });
  const shopCategories = categoriesFor(brandProducts);
  const shopCategoryCounts = Object.fromEntries(
    shopCategories.map((c) => [c, filterShopProducts(brandProducts, { category: c }).length]),
  );
  const shopProducts = filterShopProducts(brandProducts, {
    category: shopCategories.includes(shopCategory) ? shopCategory : null,
  });
  const [productsLoading, setProductsLoading] = useState(true);
  // Per-product role pricing map: { [product_id]: { msrp, role_price, weight } }
  const [pricingByProduct, setPricingByProduct] = useState({});
  const HOUSE_SLUGS = new Set(["orevital", "mindfulminerals", "house"]);
  const isHouse = HOUSE_SLUGS.has(String(slug || "").toLowerCase());
  // Honour ?view=home|shop so the product page's nav links can route into
  // a specific tab on the dealer page.
  const initialView = (() => {
    const v = new URLSearchParams(window.location.search).get("view");
    if (["home", "shop", "about", "product", "protocols", "form", "qa"].includes(v)) return v;
    return isHouse ? "shop" : "home";
  })();
  const [view, setView] = useState(initialView); // "home" | "shop" | "about" | "product"
  const [productSlug, setProductSlug] = useState(
    () => new URLSearchParams(window.location.search).get("p") || null
  );
  // In-page <Link>s (product page → Protocols, concern chips, nav) change only the
  // query string; React Router keeps this component mounted, so sync view/p from it.
  const location = useLocation();
  const [protocolsKey, setProtocolsKey] = useState(0);
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const v = q.get("view");
    if (!v) return;
    if (v === "product") {
      const p = q.get("p");
      if (p) setProductSlug(p);
      setView("product");
    } else if (["home", "shop", "about", "protocols", "form", "qa"].includes(v)) {
      setView((cur) => (cur === v && v !== "protocols" ? cur : v));
      setProtocolsKey((k) => k + 1);
    }
    window.scrollTo({ top: 0 });
  }, [location.search]);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [cartMap, setCartMap] = useState({});
  const [stripePaid, setStripePaid] = useState(null);
  const [commentProduct, setCommentProduct] = useState(null);
  const [commentText, setCommentText] = useState("");

  // Handle the return from Stripe hosted checkout (?stripe_session=...): poll the
  // payment status, clear the cart on success, and show a confirmation.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session");
    if (!sid) return undefined;
    let stopped = false;
    let tries = 0;
    const cleanUrl = () => {
      const p = new URLSearchParams(window.location.search);
      p.delete("stripe_session");
      const qs = p.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (qs ? `?${qs}` : "")
      );
    };
    const poll = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const st = await getStripeStatus(sid);
        if (st.payment_status === "paid") {
          try {
            localStorage.removeItem(`postcue.cart.${slug}`);
            localStorage.removeItem(`postcue.coupon.${slug}`);
          } catch {
            /* ignore */
          }
          setCartMap({});
          setCartCount(0);
          setStripePaid({ ok: true, order: st.order_number });
          toast.success("Payment received — thank you!");
          cleanUrl();
          return;
        }
        if (st.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          cleanUrl();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (tries < 6) {
        setTimeout(poll, 2000);
      } else {
        toast("Still processing — check your email for confirmation.");
        cleanUrl();
      }
    };
    poll();
    return () => {
      stopped = true;
    };
    // eslint-disable-next-line
  }, []);

  // Read cart qty so the header badge stays in sync after add-to-cart
  useEffect(() => {
    function syncCount() {
      try {
        const raw = localStorage.getItem(`postcue.cart.${slug}`);
        const c = raw ? JSON.parse(raw) : {};
        // Keep a stable reference for the Airtable sync effect: only swap when
        // the cart actually changed, so the 800ms poll doesn't reset the debounce.
        setCartMap((prev) =>
          JSON.stringify(prev) === JSON.stringify(c) ? prev : c
        );
        // Include BOGO free units in the badge count so it matches what
        // actually ships (4 = 3 paid + 1 free).
        const productMap = {};
        for (const p of products || []) productMap[p.id] = p;
        let total = 0;
        for (const [id, paidQ] of Object.entries(c)) {
          const paid = Number(paidQ) || 0;
          total += paid;
          const promo = productMap[id]?.promo;
          if (paid > 0 && promo && promo.active) {
            const buy = Number(promo.buy_qty || 0);
            const free = Number(promo.free_qty || 0);
            if (buy > 0 && free > 0) {
              total += Math.floor(paid / buy) * free;
            }
          }
        }
        setCartCount(total);
      } catch {
        setCartCount(0);
      }
    }
    syncCount();
    window.addEventListener("storage", syncCount);
    const id = setInterval(syncCount, 800);
    return () => {
      window.removeEventListener("storage", syncCount);
      clearInterval(id);
    };
  }, [slug, products]);

  useEffect(() => {
    const ac = new AbortController();
    const run = async () => {
      setLoading(true);
      try {
        const d = await fetchPublicDealer(slug);
        if (ac.signal.aborted) return;
        setData(d);
        setError(null);
      } catch (e) {
        if (ac.signal.aborted) return;
        setError(e?.response?.data?.detail || e.message || "Could not load");
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    run();
    return () => ac.abort();
  }, [slug]);

  // Always load the OreVital catalog — products are shown by default.
  // Per-role visibility: pass the viewer's tier + record id so the backend
  // hides products that aren't visible for their role (admins see all,
  // logged-out/untiered visitors are treated as the customer role).
  useEffect(() => {
    let active = true;
    const tier = (user?.role_name || user?.role || "").toLowerCase();
    const callerUserRecordId = user?.user_record_id || user?.record_id || "";
    (async () => {
      try {
        setProductsLoading(true);
        const res = await searchProducts("", 50, { tier, callerUserRecordId });
        if (!active) return;
        setProducts(res.products || []);
      } catch {
        if (active) setProducts([]);
      } finally {
        if (active) setProductsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.role_name, user?.role, user?.user_record_id, user?.record_id]);

  // Resolve role-tier pricing for the signed-in user.  Public visitors
  // see MSRP only; signed-in dealers/spas/ambassadors get their own
  // role price from the Pricing table.  Cached per product on the
  // backend, so subsequent renders are <10ms.
  /* eslint-disable */
  useEffect(() => {
    const tier = (user?.role_name || user?.role || "").toLowerCase();
    if (!tier || products.length === 0) return;
    let active = true;
    (async () => {
      try {
        const res = await lookupPricing({
          productRecordIds: products.map((p) => p.id),
          tier,
        });
        if (!active) return;
        const map = {};
        for (const it of res?.items || []) {
          map[it.product_record_id] = it;
        }
        setPricingByProduct(map);
      } catch {
        /* fall back to MSRP only */
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.role_name, user?.role, products.length]);
  /* eslint-enable */

  // Hydrate the local cart from the shopper's saved Airtable cart the first time a
  // user is seen in this browser (new device, or an admin impersonating them). Until
  // that finishes the Airtable sync below stays OFF, so an empty local cart can never
  // wipe the server cart.
  const cartHydratedForRef = useRef(null);
  /* eslint-disable */
  useEffect(() => {
    const recId = user?.user_record_id || user?.record_id;
    if (!recId) { cartHydratedForRef.current = null; return undefined; }
    if (cartHydratedForRef.current === recId) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await fetchCart(recId);
        if (!active) return;
        const key = `postcue.cart.${slug}`;
        const ownerKey = `postcue.cart.owner.${slug}`;
        let local = {};
        try { local = JSON.parse(localStorage.getItem(key) || "{}") || {}; } catch { local = {}; }
        // A different account was using this browser's cart (admin → dealer switch):
        // never carry their items over — start from this user's own saved cart.
        let prevOwner = "";
        try { prevOwner = localStorage.getItem(ownerKey) || ""; } catch { /* ignore */ }
        if (prevOwner && prevOwner !== recId) {
          local = {};
          try { localStorage.removeItem(key); localStorage.removeItem(`postcue.coupon.${slug}`); } catch { /* ignore */ }
        }
        try { localStorage.setItem(ownerKey, recId); } catch { /* ignore */ }
        const merged = { ...local };
        for (const it of res?.items || []) {
          if (!(it.product_record_id in merged) && Number(it.quantity) > 0) merged[it.product_record_id] = Number(it.quantity);
        }
        if (JSON.stringify(merged) !== JSON.stringify(local)) {
          try { localStorage.setItem(key, JSON.stringify(merged)); } catch { /* ignore */ }
          setCartMap(merged);
        }
        if (res?.coupon_code && !localStorage.getItem(`postcue.coupon.${slug}`)) {
          try { localStorage.setItem(`postcue.coupon.${slug}`, JSON.stringify({ code: res.coupon_code })); } catch { /* ignore */ }
        }
      } catch {
        /* keep local cart; sync stays off until a later successful hydrate */
        return;
      }
      if (active) cartHydratedForRef.current = recId;
    })();
    return () => { active = false; };
  }, [user?.user_record_id, user?.record_id, slug]);
  /* eslint-enable */

  // Sync signed-in shoppers' carts to Airtable (debounced) so they show up in
  // the CRM Pending Carts view. This lives here (not in the cart drawer) because
  // DealerPage stays mounted while browsing, so items added from the product
  // grid sync even when the drawer is closed. Anonymous visitors are skipped.
  /* eslint-disable */
  useEffect(() => {
    const recId = user?.user_record_id || user?.record_id;
    if (!recId) return undefined;
    if (cartHydratedForRef.current !== recId) return undefined;
    const pids = Object.keys(cartMap).filter((id) => Number(cartMap[id]) > 0);
    let coupon = "";
    try {
      const raw = localStorage.getItem(`postcue.coupon.${slug}`);
      if (raw) coupon = JSON.parse(raw).code || "";
    } catch {
      /* ignore */
    }
    const priceFor = (id) => {
      const pr = pricingByProduct[id];
      if (pr?.role_price) return pr.role_price;
      if (pr?.msrp) return pr.msrp;
      const prod = (products || []).find((x) => x.id === id);
      return Number(prod?.price || 0);
    };
    const t = setTimeout(() => {
      const items = pids.map((id) => ({
        product_record_id: id,
        quantity: cartMap[id],
        price: priceFor(id),
      }));
      syncCart(recId, items, coupon).catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [cartMap, pricingByProduct, products, user, slug]);
  /* eslint-enable */

  function openProduct(product) {
    const ps = product.slug || product.id;
    setProductSlug(ps);
    setView("product");
    try {
      window.history.replaceState(
        null,
        "",
        `/d/${slug}?view=product&p=${encodeURIComponent(ps)}`
      );
    } catch {
      /* ignore */
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function backToShop() {
    setView("shop");
    setProductSlug(null);
    try {
      window.history.replaceState(null, "", `/d/${slug}?view=shop`);
    } catch {
      /* ignore */
    }
  }


  function handleAddToCart(product) {
    // Persist intent so signed-in return-trip still has the item.
    try {
      const key = `postcue.cart.${slug}`;
      const raw = localStorage.getItem(key);
      const cart = raw ? JSON.parse(raw) : {};
      cart[product.id] = (cart[product.id] || 0) + 1;
      localStorage.setItem(key, JSON.stringify(cart));
      setCartCount(
        Object.values(cart).reduce((s, q) => s + (Number(q) || 0), 0)
      );
    } catch {
      /* ignore quota / private-mode errors */
    }
    if (!user) {
      // Capture the anonymous cart in Airtable before we send them to sign in.
      try {
        const cart = JSON.parse(localStorage.getItem(`postcue.cart.${slug}`) || "{}");
        const items = Object.keys(cart).filter((id) => Number(cart[id]) > 0).map((id) => {
          const pr = pricingByProduct[id];
          const prod = (products || []).find((x) => x.id === id);
          return { product_record_id: id, quantity: cart[id], price: pr?.role_price || pr?.msrp || Number(prod?.price || 0), name: prod?.name || "" };
        });
        guestSyncCart(slug, items).catch(() => {});
      } catch { /* ignore */ }
      navigate(`/login?next=${encodeURIComponent(`/d/${slug}`)}`);
      return;
    }
    setCartOpen(true);
  }

  if (loading) {
    return (
      <div className="min-h-dvh bg-zinc-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-dvh bg-zinc-50 flex flex-col items-center justify-center px-6 text-center">
        <AlertCircle className="text-zinc-400 mb-3" size={32} />
        <div className="font-semibold text-zinc-900">Dealer not found</div>
        <p className="text-sm text-zinc-500 mt-1 max-w-sm">
          We couldn&apos;t find a dealer page for <span className="font-mono">{slug}</span>.
        </p>
        <Link to="/" className="text-sm text-violet-700 mt-4 hover:underline">
          ← Back to home
        </Link>
      </div>
    );
  }

  const fullName = isHouse
    ? "OreVital"
    : `${data.first_name || ""} ${data.last_name || ""}`.trim();
  const initials = isHouse
    ? "OV"
    : `${(data.first_name?.[0] || "?").toUpperCase()}${
        (data.last_name?.[0] || "").toUpperCase()
      }`;

  const socials = [
    { url: data.dealer_instagram, icon: Instagram, label: "Instagram" },
    { url: data.dealer_facebook, icon: Facebook, label: "Facebook" },
    { url: data.dealer_twitter, icon: Twitter, label: "Twitter" },
    { url: data.youtube, icon: Youtube, label: "YouTube" },
  ].filter((s) => s.url);

  return (
    <div className="min-h-dvh bg-zinc-50 pb-16" data-testid="dealer-page">
      {stripePaid?.ok && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          data-testid="stripe-success-overlay"
          onClick={() => setStripePaid(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <Star className="text-emerald-600" size={26} />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900">Payment received</h3>
            <p className="mt-1 text-sm text-zinc-500">
              Thank you! Your order{stripePaid.order ? ` #${stripePaid.order}` : ""} is
              confirmed and now processing.
            </p>
            <Button
              className="mt-5 w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setStripePaid(null)}
              data-testid="stripe-success-close"
            >
              Continue shopping
            </Button>
          </div>
        </div>
      )}
      {/* Header bar — logo left, nav right. Identical template to Blog / Landing / Login. */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-3">
          <button onClick={() => setView("home")} className="flex items-center shrink-0" data-testid="dealer-logo">
            <BrandLogo alt={brand.company} />
          </button>
          <nav
            className="flex items-center gap-1 sm:gap-2 overflow-x-auto min-w-0 flex-1 sm:justify-end"
            data-testid="dealer-nav"
          >
            <button
              onClick={() => setView("home")}
              className={`shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${
                view === "home"
                  ? "bg-violet-600 text-white shadow-sm"
                  : "text-zinc-700 hover:bg-zinc-100"
              }`}
              data-testid="nav-home"
            >
              <HomeIcon size={14} />
              <span className="hidden sm:inline">Home</span>
            </button>
            <button
              onClick={() => setView("shop")}
              className={`shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full transition-colors ${
                view === "shop"
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              }`}
              data-testid="nav-brands"
            >
              <ShoppingBag size={14} />
              Shop
            </button>
            <Link
              to="/blog"
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full text-zinc-700 hover:bg-zinc-100 transition-colors"
              data-testid="nav-blog"
            >
              <ChefHat size={14} />
              <span className="hidden sm:inline">Blog</span>
            </Link>
            <Link
              to={`/d/${slug}/events`}
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-full text-emerald-700 hover:bg-emerald-50 transition-colors"
              data-testid="nav-events"
            >
              <CalendarDays size={14} />
              <span className="hidden sm:inline">Events</span>
            </Link>
            <button
              onClick={() => {
                if (!user) {
                  navigate(`/login?next=${encodeURIComponent(`/d/${slug}`)}`);
                  return;
                }
                setCartOpen(true);
              }}
              className="relative shrink-0 h-10 w-10 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 flex items-center justify-center"
              data-testid="nav-cart"
              aria-label="Open cart"
            >
              <ShoppingCart size={16} />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                  data-testid="nav-cart-badge"
                >
                  {cartCount}
                </span>
              )}
            </button>
          </nav>
          {/* Sign in sits outside the scrolling strip so it's always visible on phones */}
          <Link
            to={
              user
                ? "/app/earnings"
                : `/login?next=${encodeURIComponent(`/d/${slug}`)}`
            }
            className="shrink-0 h-10 w-10 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 flex items-center justify-center"
            data-testid="dealer-page-signin-link"
            aria-label={user ? "Account" : "Sign in"}
            title={user ? "Account" : "Sign in"}
          >
            <UserRound size={16} />
          </Link>
        </div>
      </header>

      <TagsMenuBar />

      <main className="max-w-6xl mx-auto px-4 pt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div className="space-y-5">
        {view === "home" && (
          <>
            <BrandHero />

            <button
              type="button"
              onClick={() => { setView("protocols"); try { window.history.replaceState(null, "", `/d/${slug}?view=protocols`); } catch { /* ignore */ } window.scrollTo({ top: 0 }); }}
              className="w-full text-left rounded-2xl bg-[#0b2545] text-white p-4 sm:p-5 flex items-center gap-3 hover:bg-[#123566] transition-colors"
              data-testid="home-protocol-card"
            >
              <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Sparkles size={18} className="text-[#d6ad52]" /></span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-bold">Build Your OreVital Protocol</span>
                <span className="block text-xs text-white/75">Pick your focus — find the peptides and products that fit your routine.</span>
              </span>
              <ArrowRightIcon size={16} className="shrink-0 text-white/70" />
            </button>

            {/* Existing dynamic post tabs row + content */}
            <PostTabs
              homeHtml={mergeAboutIntoHome(data.home_html, data.aboutus, data.company_name || data.company || "")}
              posts={data.home_posts || []}
              dealerName={fullName}
              dealerSlug={slug}
            />
          </>
        )}

        {view === "about" && (
          <>
            {/* vCard */}
            <Card className="rounded-2xl border-zinc-200 overflow-hidden p-0">
              <div className="bg-gradient-to-br from-violet-600 via-violet-700 to-fuchsia-700 h-24" />
              <div className="px-5 pb-5 -mt-12">
                <div className="flex items-end gap-4">
                  {data.image_url ? (
                    <img
                      src={data.image_url}
                      alt={fullName}
                      className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      data-testid="dealer-photo"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-violet-600 text-white flex items-center justify-center text-3xl font-semibold border-4 border-white shadow">
                      {initials}
                    </div>
                  )}
                  {data.dealer_logo && (
                    <img
                      src={data.dealer_logo}
                      alt="Logo"
                      className="h-10 mb-2 max-w-[120px]"
                    />
                  )}
                </div>
                <div className="mt-3">
                  <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
                    {fullName || "Dealer"}
                  </h1>
                  {data.company_name && (
                    <div className="text-sm text-zinc-500 mt-0.5">{data.company_name}</div>
                  )}
                  {(data.city || data.state) && (
                    <div className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                      <MapPin size={11} />
                      {[data.city, data.state].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {data.website_url && (
                    <a
                      href={data.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                      data-testid="dealer-website-link"
                    >
                      <Globe size={12} /> Website
                    </a>
                  )}
                  <a
                    href={`mailto:${data.email || ""}`}
                    className="text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-full px-3 py-1.5 flex items-center gap-1.5"
                  >
                    <Mail size={12} /> Contact
                  </a>
                </div>

                {socials.length > 0 && (
                  <div className="mt-4 flex items-center gap-2">
                    {socials.map(({ url, icon: Icon, label }) => (
                      <a
                        key={label}
                        href={url.startsWith("http") ? url : `https://${url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 rounded-full bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center justify-center"
                        aria-label={label}
                      >
                        <Icon size={16} />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            {/* About Us widget */}
            {data.aboutus && (
              <Card
                id="about"
                className="rounded-2xl border-zinc-200 p-5 scroll-mt-20"
                data-testid="dealer-aboutus"
              >
                <div className="text-[10px] uppercase tracking-widest text-violet-700 font-semibold mb-2">
                  About
                </div>
                <div
                  className="prose prose-sm max-w-none text-zinc-800"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.aboutus) }}
                />
              </Card>
            )}
          </>
        )}

        {view === "shop" && (
          <>
            <ShopFilterHero
              brand={shopBrand}
              onBrand={(b) => { setShopBrand(b); setShopCategory(null); }}
              categories={shopCategories}
              categoryCounts={shopCategoryCounts}
              category={shopCategory}
              onCategory={setShopCategory}
              query={shopQuery}
              onQuery={setShopQuery}
              resultCount={productsLoading ? null : shopProducts.length}
              searchingAllBrands={searching}
            />
            <section data-testid="dealer-brands-section">
            {productsLoading ? (
              <div className="py-10 text-center text-zinc-500 text-sm">
                <Loader2 className="animate-spin inline mr-1" size={14} /> Loading products…
              </div>
            ) : shopProducts.length === 0 ? (
              <div className="py-10 text-center text-zinc-500 text-sm" data-testid="shop-empty">
                {products.length === 0 ? "No products available right now." : "No products match your filters."}
              </div>
            ) : (
              <ul
                className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                data-testid="dealer-products-grid"
              >
                {shopProducts.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    pricing={pricingByProduct[p.id]}
                    dealerSlug={slug}
                    onAdd={() => handleAddToCart(p)}
                    onComment={() => setCommentProduct(p)}
                    onOpen={() => openProduct(p)}
                    shareUrl={productShareUrl(p)}
                    copyLabel={myHandle ? "Copy my product link" : "Copy product link"}
                    copySuccess={myHandle ? "Your product link copied — share it to earn on this sale" : "Product link copied"}
                  />
                ))}
              </ul>
            )}
            </section>
          </>
        )}

        {view === "protocols" && (
          <ProtocolBuilder
            key={protocolsKey}
            dealerSlug={slug}
            pricingByProduct={pricingByProduct}
            onOpenProduct={openProduct}
            initialFocus={focusFromSearch()}
          />
        )}

        {view === "form" && (
          <DealerFormView token={new URLSearchParams(location.search).get("t") || ""} />
        )}

        {view === "qa" && (
          <DealerQaView slug={slug} dealerName={fullName} />
        )}

        {view === "product" && productSlug && (
          <ProductDetailContent
            dealerSlug={slug}
            productSlug={productSlug}
            user={user}
            onAdded={() => setCartOpen(true)}
            onBack={backToShop}
          />
        )}
        </div>

        {/* Right sidebar — Featured posts loaded from Airtable
            (featured_order > 0), shown as a single-card carousel.
            Hidden on mobile/tablet, shown on lg+ screens. */}
        <aside className="hidden lg:block" data-testid="dealer-sidebar">
          <div className="space-y-4">
            <FeaturedSidebar dealerSlug={slug} />
            <EventsFeaturedBox dealerSlug={slug} />
            <ReviewsSidebar dealerSlug={slug} />
            <VideoSidebar dealerSlug={slug} />
            <PhotosSidebar dealerSlug={slug} />
          </div>
        </aside>
      </main>

      <CartSheet
        open={cartOpen}
        onOpenChange={setCartOpen}
        dealerSlug={slug}
        products={products}
        user={user}
      />

      {/* Comment dialog — saves to localStorage for now;
          wire to Airtable comments table when schema is shared. */}
      {commentProduct && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setCommentProduct(null)}
          data-testid="product-comment-dialog"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-zinc-900 mb-1">
              Comment on {commentProduct.name}
            </h3>
            <p className="text-xs text-zinc-500 mb-3">
              Your comment will be reviewed before it&apos;s posted.
            </p>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Share your experience with this product…"
              rows={4}
              className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              data-testid="product-comment-textarea"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => setCommentProduct(null)}
                className="px-4 py-2 rounded-xl border border-zinc-200 text-sm text-zinc-700 hover:bg-zinc-50"
                data-testid="product-comment-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  try {
                    const key = `postcue.comments.${slug}`;
                    const raw = localStorage.getItem(key);
                    const all = raw ? JSON.parse(raw) : [];
                    all.push({
                      product_id: commentProduct.id,
                      product_name: commentProduct.name,
                      user_email: user?.email_id || user?.email || "",
                      user_name:
                        `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
                        "Customer",
                      text: commentText.trim(),
                      at: new Date().toISOString(),
                    });
                    localStorage.setItem(key, JSON.stringify(all));
                  } catch {
                    /* ignore */
                  }
                  setCommentProduct(null);
                }}
                disabled={!commentText.trim()}
                className="px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:bg-zinc-300 text-white text-sm font-semibold"
                data-testid="product-comment-submit"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

function ProductRow({ product, pricing, dealerSlug, onAdd }) {
  const msrp = Number(product.price || 0);
  const rolePrice = pricing?.role_price ? Number(pricing.role_price) : null;
  const outOfStock = Number(product.inventory || 0) <= 0;
  const showRolePrice =
    rolePrice !== null && rolePrice > 0 && Math.abs(rolePrice - msrp) > 0.005;
  const hasRating =
    Number(product.rating_count || 0) > 0 ||
    Number(product.rating_avg || 0) > 0;
  const ratingValue = hasRating ? Number(product.rating_avg || 5) : 5;

  // Detail page URL — prefer slug, fall back to record id
  const detailHref = `/d/${encodeURIComponent(dealerSlug)}/product/${encodeURIComponent(
    product.slug || product.id
  )}`;

  return (
    <li
      className="rounded-2xl border border-zinc-200 bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      data-testid={`product-row-${product.id}`}
    >
      <div className="flex gap-3 sm:gap-4 p-3 sm:p-4">
        {/* Large image left — clickable to detail */}
        <Link
          to={detailHref}
          className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-zinc-50 flex items-center justify-center overflow-hidden"
          data-testid={`product-image-${product.id}`}
        >
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <Package className="text-zinc-300" size={48} />
          )}
        </Link>

        {/* Details right */}
        <div className="flex-1 min-w-0 flex flex-col">
          <Link
            to={detailHref}
            className="text-base sm:text-lg font-bold text-zinc-900 leading-tight tracking-tight hover:text-violet-700 line-clamp-2"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
          >
            {product.name}
          </Link>

          {/* Rating */}
          <div
            className={`flex items-center gap-1 mt-1 ${
              hasRating ? "" : "opacity-60"
            }`}
          >
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                size={12}
                className={
                  i <= Math.round(ratingValue)
                    ? "fill-amber-400 text-amber-400"
                    : "text-zinc-300"
                }
              />
            ))}
            <span className="text-[11px] text-zinc-500 ml-1">
              {hasRating
                ? `${ratingValue.toFixed(1)} (${product.rating_count})`
                : "New"}
            </span>
          </div>

          {/* BOGO promo badge */}
          {product.promo && product.promo.active && (
            <span
              className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] sm:text-xs font-bold bg-rose-100 text-rose-700 self-start"
              data-testid={`product-promo-${product.id}`}
            >
              🎁 {product.promo.label}
            </span>
          )}

          {/* Blurb / excerpt */}
          {(product.blurb || product.excerpt) && (
            <p className="text-xs sm:text-sm text-zinc-600 leading-snug mt-1.5 line-clamp-2">
              {product.blurb || product.excerpt}
            </p>
          )}

          {/* Price + role price */}
          <div className="mt-auto pt-2 flex items-end justify-between gap-2 flex-wrap">
            <div className="flex flex-col leading-tight">
              {showRolePrice ? (
                <>
                  <span className="text-[10px] uppercase tracking-wide text-zinc-400">
                    MSRP <span className="line-through">{fmtUSD(msrp)}</span>
                  </span>
                  <span
                    className="text-xl sm:text-2xl font-extrabold text-emerald-700"
                    style={{
                      fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                    }}
                  >
                    {fmtUSD(rolePrice)}
                  </span>
                  <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">
                    Your price
                  </span>
                </>
              ) : (
                <span
                  className="text-xl sm:text-2xl font-extrabold text-violet-700"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
                  }}
                >
                  {fmtUSD(msrp)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={detailHref}
                className="h-9 px-3 rounded-xl border border-zinc-200 text-zinc-600 hover:bg-zinc-50 flex items-center gap-1 text-xs font-medium"
                data-testid={`product-details-${product.id}`}
              >
                Details
                <ChevronRight size={12} />
              </Link>
              {outOfStock ? (
                <span
                  className="h-9 px-3 rounded-xl bg-rose-50 text-rose-600 border border-rose-200 flex items-center text-xs font-semibold"
                  data-testid={`product-oos-${product.id}`}
                >
                  Out of stock
                </span>
              ) : (
                <button
                  onClick={onAdd}
                  className="h-9 px-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white flex items-center gap-1 text-xs font-semibold shadow-sm"
                  data-testid={`product-addcart-${product.id}`}
                >
                  <ShoppingCart size={13} />
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

function ProductCard({ product, pricing, onAdd, onComment, onOpen, shareUrl, copyLabel = "Copy product link", copySuccess = "Product link copied" }) {
  const hasRating =
    Number(product.rating_count || 0) > 0 ||
    Number(product.rating_avg || 0) > 0;
  const msrp = Number(product.price || 0);
  const rolePrice = pricing?.role_price ? Number(pricing.role_price) : null;
  const onSale = !!pricing?.on_sale && Number(pricing?.sale_price || 0) > 0;
  const salePrice = onSale ? Number(pricing.sale_price) : null;
  const showRolePrice =
    rolePrice !== null && rolePrice > 0 && Math.abs(rolePrice - msrp) > 0.005;
  const finalPrice = onSale ? salePrice : showRolePrice ? rolePrice : msrp;
  const showStrike = (onSale || showRolePrice) && finalPrice < msrp;
  const outOfStock = Number(product.inventory || 0) <= 0;
  return (
    <div
      className="rounded-3xl border border-zinc-200 bg-white p-5 flex flex-col shadow-sm hover:shadow-lg transition-shadow"
      data-testid={`product-card-${product.id}`}
    >
      {/* Product image — larger, crisp white background, clickable to detail */}
      <button
        type="button"
        onClick={onOpen}
        className="h-56 rounded-2xl bg-white border border-zinc-100 flex items-center justify-center mb-4 overflow-hidden cursor-pointer"
        data-testid={`product-open-${product.id}`}
        aria-label={`View ${product.name}`}
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="h-full w-auto object-contain p-2"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <Package className="text-zinc-300" size={64} />
        )}
      </button>

      {/* Title — larger, tighter tracking, clickable */}
      <button
        type="button"
        onClick={onOpen}
        className="text-left text-lg font-bold text-zinc-900 leading-tight tracking-tight mb-2 line-clamp-2 min-h-[2.6em] hover:text-violet-700 transition-colors"
        style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
        data-testid={`product-title-${product.id}`}
      >
        {product.name}
      </button>

      {/* Rating — blurred placeholder */}
      <div
        className={`flex items-center gap-1.5 mb-2 ${
          hasRating ? "" : "blur-[3px] select-none opacity-60"
        }`}
        aria-hidden={!hasRating}
      >
        <span className="text-amber-400 text-base tracking-tight">★★★★★</span>
        <span className="text-sm font-medium text-zinc-700">
          {hasRating
            ? `${Number(product.rating_avg || 5).toFixed(1)} (${product.rating_count})`
            : "5.0 (5)"}
        </span>
      </div>

      {/* Excerpt — larger body text */}
      <p className="text-sm text-zinc-700 leading-relaxed line-clamp-3 mb-4 flex-1">
        <span className="font-semibold text-violet-700">OreVital · </span>
        {product.excerpt ||
          `${product.name}. Premium peptide therapy.`}
      </p>

      {/* Footer: price + actions */}
      <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
        <div className="flex flex-col leading-tight">
          {showStrike && (
            <span className="text-[11px] text-zinc-400 line-through">
              {fmtUSD(msrp)}
            </span>
          )}
          <span
            className={`text-2xl font-extrabold ${onSale ? "text-rose-600" : showRolePrice ? "text-emerald-700" : "text-violet-700"}`}
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            data-testid={`product-price-${product.id}`}
          >
            {fmtUSD(finalPrice)}
          </span>
          {onSale ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-600">
              Sale price
            </span>
          ) : showRolePrice ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
              Your price
            </span>
          ) : null}
          {outOfStock && (
            <span
              className="text-[10px] font-semibold uppercase tracking-wide text-rose-600"
              data-testid={`product-oos-${product.id}`}
            >
              Out of stock
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {shareUrl && (
            <ShareMenu
              url={shareUrl}
              text={`${product.name} — OreVital`}
              copyLabel={copyLabel}
              copySuccess={copySuccess}
              testId={`product-share-${product.id}`}
            />
          )}
          <button
            onClick={onComment}
            className="h-10 w-10 rounded-xl border border-zinc-200 text-zinc-600 hover:bg-zinc-50 flex items-center justify-center"
            data-testid={`product-comment-${product.id}`}
            aria-label="Add comment"
            title="Add comment"
          >
            <MessageCircle size={16} />
          </button>
          {!outOfStock && (
            <button
              onClick={onAdd}
              className="h-10 w-10 rounded-xl bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center shadow-sm"
              data-testid={`product-addcart-${product.id}`}
              aria-label="Add to cart"
              title="Add to cart"
            >
              <ShoppingCart size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
